import {join} from 'path';

import {Identity} from 'shared/src/models/User';

const os = require('os');

export function getFilesystemFriendlyTimestamp() {
  return new Date()
    .toISOString()
    .replace(/:/g, '-') // replace : with .
    .replace(/\..+/, ''); // delete the dot and everything after
}

// Utility function that provides a location for services to dump debugging data to
export function getDumpDir(service: string) {
  return join(os.tmpdir(), service);
}

const BYTE_SHIFT_8 = 8;
const BYTE_SHIFT_24 = 24;
const BYTE_SHIFT_16 = 16;

const BYTE_MASK = 0xff;
const POWER_BASE = 2;

// reformat CIDR's IP address that's within the range to the beginning of the range
// note this function assumes valid CIDR.
export function reformatCIDR(cidr: string) {
  // Ref: https://tech.mybuilder.com/determining-if-an-ipv4-address-is-within-a-cidr-range-in-javascript/
  const ip4ToInt = (ip: string) =>
    ip.split('.').reduce((int, oct) => (int << BYTE_SHIFT_8) + parseInt(oct, 10), 0) >>> 0;

  const intToIp4 = (int: number) =>
    [
      (int >>> BYTE_SHIFT_24) & BYTE_MASK,
      (int >>> BYTE_SHIFT_16) & BYTE_MASK,
      (int >>> BYTE_SHIFT_8) & BYTE_MASK,
      int & BYTE_MASK,
    ].join('.');

  const [range, bits] = cidr.split('/');
  const BITS_IN_IPV4 = 32;
  const mask = ~(POWER_BASE ** (BITS_IN_IPV4 - Number(bits)) - 1);

  return intToIp4(ip4ToInt(range) & mask) + `/${bits}`;
}

export function isCIDR(cidr: string): boolean {
  // https://github.com/sindresorhus/ip-regex/blob/main/index.js
  const ipv4 = '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)(?:\\.(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)){3}';
  // https://github.com/silverwind/cidr-regex/blob/master/index.js
  const cidrRegex = new RegExp(`^${ipv4}\\/(3[0-2]|[12]?[0-9])$`);

  return cidrRegex.test(cidr);
}

export function dedupeUrlSlash(url: string, path: string) {
  if (url?.endsWith('/')) {
    return `${url}${path}`;
  } else {
    return `${url}/${path}`;
  }
}

export function isTestAutomationUser(email: string | undefined): boolean {
  if (!email) {
    return false;
  }

  return isTestE2EUser(email) || isTestIntegrationUser(email) || isTestSDETUser(email);
}

export function isTestE2EUser(email: string): boolean {
  return email?.startsWith('testautomation');
}

export function isTestIntegrationUser(email: string): boolean {
  return email?.startsWith('testintegration');
}

export function isTestSDETUser(email: string): boolean {
  return email.startsWith('sdetautomationcribluser') ||
    (email.startsWith('sdet') && email.includes('@sdet'));
}

export function isCriblInternalEmail(email: string): boolean {
  return email.toLocaleLowerCase().endsWith('@cribl.io');
}

export function isCriblTestEmail(email: string): boolean {
  return email.toLocaleLowerCase().endsWith('@testcribl.io');
}

export const semverCompare = (a: string, b: string) => {
  // regex for extracting major, minor, patch and release versions
  // from semantic version (4.0.3-latest)
  const semverRegex = /(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:-(.*))?/;
  const [aMajor, aMinor, aPatch, aRelease] = a.split(semverRegex);
  const [bMajor, bMinor, bPatch, bRelease] = b.split(semverRegex);

  if (parseInt(aMajor, 10) > parseInt(bMajor, 10)) {
    return 1;
  } else if (parseInt(aMajor, 10) < parseInt(bMajor, 10)) {
    return -1;
  }

  if (parseInt(aMinor, 10) > parseInt(bMinor, 10)) {
    return 1;
  } else if (parseInt(aMinor, 10) < parseInt(bMinor, 10)) {
    return -1;
  }

  if (parseInt(aPatch, 10) > parseInt(bPatch, 10)) {
    return 1;
  } else if (parseInt(aPatch, 10) < parseInt(bPatch, 10)) {
    return -1;
  }

  return ('' + aRelease).localeCompare(bRelease);
};

/**
 * Counts the number of items in an enum
 * @param enumName
 * @return number
 */
export function enumElementCount(enumName: any): number {
  let count = 0;

  for (const item in enumName) {
    if (isNaN(Number(item))) count++;
  }

  return count;
}

export const awsRegionsRegex = new RegExp('(us(-gov)?|ap|ca|cn|eu|sa)-(central|(north|south)?(east|west)?)-\\d', 'm');

export function isValidAwsRegion(regionName: string, regionList?: string[]): boolean {
  if (!awsRegionsRegex.test(regionName)) return false;

  return !regionList || regionList.includes(regionName);
}

export enum SSOMethod {
  SAML = 'samlp',
  OIDC = 'oidc',
}

export const isSsoIdentity = (identity: Identity) => {
  return Object.values(SSOMethod).includes(identity.provider as SSOMethod);
};

export enum IdentityProvider {
  AUTH0 = 'auth0',
  OKTA = 'okta',
}

export const isValidUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
